import { useState, useRef, useCallback, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Document, Page, pdfjs } from "react-pdf";
import { cn } from "@/zapi-frontend/shared/lib/utils";
import { Typography, InnoveraLoading } from "@libs/zapi-ui/custom";
import $api from "@/zapi-frontend/shared/api/axios";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

type TPdfPreviewProps = {
  pdfUrl: string | null;
  slideTitles?: string[];
  initialPage?: number;
  onPageChange?: (page: number) => void;
  isPageLoading?: boolean;
};

const MAX_RETRIES = 5;

export function PdfPreview(props: TPdfPreviewProps) {
  const { pdfUrl, slideTitles = [], initialPage = 1, onPageChange, isPageLoading } = props;
  const { id: projectId } = useParams();
  const [presignedUrl, setPresignedUrl] = useState<string | null>(null);
  const [isUrlLoading, setIsUrlLoading] = useState(true);
  const [numPages, setNumPages] = useState<number>(0);
  const [selectedPage, setSelectedPage] = useState<number>(initialPage);
  const [isLoading, setIsLoading] = useState(true);
  const [retryKey, setRetryKey] = useState(0);
  const retryCountRef = useRef(0);
  const mainContainerRef = useRef<HTMLDivElement>(null);
  const pageRefs = useRef<Map<number, HTMLDivElement>>(new Map());
  const thumbnailRefs = useRef<Map<number, HTMLDivElement>>(new Map());
  const isProgrammaticScrollRef = useRef(false);
  const hasScrolledToInitialRef = useRef(false);

  useEffect(() => {
    async function fetchPresignedUrl() {
      if (!pdfUrl || !projectId) {
        setIsUrlLoading(false);
        return;
      }

      setPresignedUrl(null);
      setIsUrlLoading(true);
      setIsLoading(true);
      setNumPages(0);
      setRetryKey(0);
      retryCountRef.current = 0;
      hasScrolledToInitialRef.current = false;

      try {
        const response = await $api.get<{ data: { url: string } }>(
          `/projects/${projectId}/presentations/download-url`,
          { params: { fileType: "pdf" } }
        );
        setPresignedUrl(response.data.data.url);
      } catch (error) {
        console.error("Failed to fetch presigned URL:", error);
        setPresignedUrl(pdfUrl);
      } finally {
        setIsUrlLoading(false);
      }
    }

    fetchPresignedUrl();
  }, [pdfUrl, projectId]);

  function onDocumentLoadSuccess({ numPages }: { numPages: number }) {
    setNumPages(numPages);
    setIsLoading(false);
    retryCountRef.current = 0;
  }

  function onDocumentLoadError(error: Error) {
    console.error("Error loading PDF:", error);
    if (retryCountRef.current < MAX_RETRIES) {
      retryCountRef.current += 1;
      setTimeout(() => {
        setRetryKey((prev) => prev + 1);
      }, 1000);
    } else {
      setIsLoading(false);
    }
  }

  const handleThumbnailClick = useCallback((pageNum: number) => {
    isProgrammaticScrollRef.current = true;
    setSelectedPage(pageNum);

    const pageElement = pageRefs.current.get(pageNum);
    if (pageElement) {
      pageElement.scrollIntoView({ behavior: "smooth", block: "start" });
      setTimeout(() => {
        isProgrammaticScrollRef.current = false;
      }, 500);
    }
  }, []);

  useEffect(() => {
    if (numPages === 0) return;
    const thumbnailElement = thumbnailRefs.current.get(selectedPage);
    if (thumbnailElement) thumbnailElement.scrollIntoView({ behavior: "smooth", block: "nearest" });
    onPageChange?.(selectedPage);
  }, [selectedPage, onPageChange, numPages]);

  useEffect(() => {
    if (numPages === 0 || hasScrolledToInitialRef.current || initialPage <= 1) return;

    hasScrolledToInitialRef.current = true;
    setSelectedPage(initialPage);

    setTimeout(() => {
      isProgrammaticScrollRef.current = true;
      const pageElement = pageRefs.current.get(initialPage);
      if (pageElement) {
        pageElement.scrollIntoView({ behavior: "auto", block: "start" });
      }
      const thumbnailElement = thumbnailRefs.current.get(initialPage);
      if (thumbnailElement) {
        thumbnailElement.scrollIntoView({ behavior: "auto", block: "nearest" });
      }
      setTimeout(() => {
        isProgrammaticScrollRef.current = false;
      }, 100);
    }, 50);
  }, [numPages, initialPage]);

  useEffect(() => {
    const container = mainContainerRef.current;
    if (!container || numPages === 0) return;

    const findCentermostPage = () => {
      if (isProgrammaticScrollRef.current) return;

      const containerRect = container.getBoundingClientRect();
      const containerCenter = containerRect.top + containerRect.height / 2;

      let closestPage = 1;
      let closestDistance = Infinity;

      for (let i = 1; i <= numPages; i++) {
        const el = pageRefs.current.get(i);
        if (!el) continue;

        const rect = el.getBoundingClientRect();
        const elCenter = rect.top + rect.height / 2;
        const distance = Math.abs(containerCenter - elCenter);

        if (distance < closestDistance) {
          closestDistance = distance;
          closestPage = i;
        }
      }

      setSelectedPage(closestPage);
    };

    container.addEventListener("scroll", findCentermostPage);
    return () => container.removeEventListener("scroll", findCentermostPage);
  }, [numPages]);

  const getPageTitle = (pageNum: number) => {
    if (slideTitles[pageNum - 1]) return slideTitles[pageNum - 1];

    return `Slide ${pageNum}`;
  };

  if (isUrlLoading || isPageLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-4">
        <InnoveraLoading size={60} />
        <Typography variant="body2" color="zinc500">
          Loading presentation...
        </Typography>
      </div>
    );
  }

  if (!pdfUrl) return null;
  if (!presignedUrl) {
    return (
      <div className="flex items-center justify-center h-full">
        <Typography variant="body2" color="zinc500">
          Failed to load PDF
        </Typography>
      </div>
    );
  }

  return (
    <div className="flex w-full h-full gap-3">
      {/* Thumbnails Sidebar */}
      <div className={cn("w-[220px] flex flex-col overflow-hidden", isLoading && "hidden")}>
        <div className="flex-1 overflow-y-auto custom-thin-scrollbar">
          <Document
            key={`thumb-${retryKey}`}
            file={presignedUrl}
            onLoadSuccess={onDocumentLoadSuccess}
            onLoadError={onDocumentLoadError}
          >
            {Array.from({ length: numPages }, (_, index) => {
              const pageNum = index + 1;
              return (
                <div
                  key={`thumb-${pageNum}`}
                  ref={(el) => {
                    if (el) thumbnailRefs.current.set(pageNum, el);
                  }}
                  onClick={() => handleThumbnailClick(pageNum)}
                  className={cn(
                    "mb-2 cursor-pointer rounded-lg overflow-hidden border-2 transition-all",
                    selectedPage === pageNum
                      ? "border-innoveraOrange-400 shadow-md"
                      : "border-transparent hover:border-innoveraZinc-200"
                  )}
                >
                  <div className="bg-white">
                    <Page
                      pageNumber={pageNum}
                      width={204}
                      renderTextLayer={false}
                      renderAnnotationLayer={false}
                      loading={<div className="w-full h-[110px] bg-innoveraZinc-100 animate-pulse" />}
                    />
                  </div>
                  <div
                    className={cn(
                      "px-2 py-1.5 bg-white border-t",
                      selectedPage === pageNum ? "border-innoveraOrange-200" : "border-innoveraZinc-100"
                    )}
                  >
                    <Typography
                      variant="label4"
                      className={cn(
                        "truncate block",
                        selectedPage === pageNum ? "text-innoveraOrange-600" : "text-innoveraZinc-700"
                      )}
                    >
                      {getPageTitle(pageNum)}
                    </Typography>
                  </div>
                </div>
              );
            })}
          </Document>
        </div>
      </div>

      {/* Main PDF View */}
      <div ref={mainContainerRef} className="w-full overflow-y-auto custom-thin-scrollbar flex justify-center">
        {(isLoading || isPageLoading) && (
          <div className="flex flex-col items-center justify-center h-full gap-4">
            <InnoveraLoading size={60} />
            <Typography variant="body2" color="zinc500">
              Loading presentation...
            </Typography>
          </div>
        )}

        <Document
          key={`main-${retryKey}`}
          file={presignedUrl}
          onLoadSuccess={onDocumentLoadSuccess}
          onLoadError={onDocumentLoadError}
          loading={null}
          className={cn(isLoading && "hidden", "w-full")}
        >
          <div className="w-full flex flex-col items-center gap-4">
            {Array.from({ length: numPages }, (_, index) => {
              const pageNum = index + 1;
              return (
                <div
                  key={`page-${pageNum}`}
                  ref={(el) => {
                    if (el) pageRefs.current.set(pageNum, el);
                  }}
                  data-page-num={pageNum}
                  className={cn(
                    "w-full border-[3px] border-transparent",
                    selectedPage === pageNum && "border-innoveraOrange-400"
                  )}
                >
                  <Page
                    pageNumber={pageNum}
                    renderTextLayer={true}
                    renderAnnotationLayer={true}
                    className="w-full [&_canvas]:!w-full [&_canvas]:!h-auto"
                    loading={
                      <div className="w-full h-fullmax-w-[800px] max-h-[450px] bg-white animate-pulse flex items-center justify-center">
                        <InnoveraLoading size={40} />
                      </div>
                    }
                  />
                </div>
              );
            })}
          </div>
        </Document>
      </div>
    </div>
  );
}
